import { Search } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ApiError, getUserPhone } from "@/api/client";
import type { UserPhone } from "@/api/types";
import { useAuthToken } from "@/auth/useAuthToken";
import { Spinner } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type LookupState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; result: UserPhone };

const SAMPLE_USER_ID = "bb5e506b-c9f7-4702-947c-b1e900c3fbd1";

export function UserPhoneLookupPage() {
  const getToken = useAuthToken();
  const [userId, setUserId] = useState("");
  const [state, setState] = useState<LookupState>({ status: "idle" });

  async function lookup(id: string) {
    const trimmed = id.trim();
    if (!trimmed) return;
    setState({ status: "loading" });
    try {
      const token = await getToken();
      const result = await getUserPhone(token, trimmed);
      setState({ status: "ready", result });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setState({ status: "ready", result: { userId: trimmed, phoneNumber: null } });
        return;
      }
      setState({
        status: "error",
        message: err instanceof Error ? err.message : "Lookup failed",
      });
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void lookup(userId);
  }

  // Fills the box and runs the lookup, so the sample lands on a real result
  // the way the UPC pages' sample buttons do.
  function onTrySample() {
    setUserId(SAMPLE_USER_ID);
    void lookup(SAMPLE_USER_ID);
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold">User lookup</h1>
        <p className="text-sm text-muted-foreground">
          Find the phone number behind a user ID.
        </p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Look up a user</CardTitle>
          <CardDescription>
            Paste a user ID to see the phone number on file.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex gap-2">
            <input
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="User ID"
              aria-label="User ID"
              className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <Button type="submit" disabled={state.status === "loading"}>
              {state.status === "loading" ? (
                <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />
              ) : (
                <Search className="h-4 w-4" aria-hidden />
              )}
              {state.status === "loading" ? "Looking up…" : "Look up"}
            </Button>
          </form>

          {state.status === "idle" ? (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={onTrySample}
            >
              Try a sample user
            </Button>
          ) : null}

          {state.status === "error" ? (
            <p className="mt-4 text-sm" style={{ color: "var(--viz-status-critical)" }}>
              {state.message}
            </p>
          ) : null}

          {state.status === "ready" ? (
            <div className="mt-6 flex flex-col items-center gap-1 rounded-md border bg-background p-6">
              <div className="text-sm text-muted-foreground">Phone number</div>
              {state.result.phoneNumber ? (
                <div className="text-3xl font-semibold">
                  {state.result.phoneNumber}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No phone number found for {state.result.userId}
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
